import { useState, useRef } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { cn } from '../lib/utils';

export const ImageUpload = ({ value, onChange, className }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Bitte nur Bilder hochladen');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Bild darf maximal 5MB groß sein');
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      const storage = getStorage();
      const fileName = `recipes/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, fileName);
      
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(prog);
        },
        (err) => {
          console.error('Upload error:', err);
          setError('Upload fehlgeschlagen');
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          onChange(downloadURL);
          setUploading(false);
          setProgress(0);
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload fehlgeschlagen');
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="image-upload-input"
      />

      {value ? (
        <div className="relative rounded-xl overflow-hidden group">
          <img
            src={value}
            alt="Vorschau"
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full"
            >
              <Upload className="w-4 h-4 mr-1" />
              Ändern
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleRemove}
              className="rounded-full"
            >
              <X className="w-4 h-4 mr-1" />
              Entfernen
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full h-48 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-terracotta hover:bg-terracotta/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="image-upload-btn"
        >
          {uploading ? (
            <>
              <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center">
                <Upload className="w-6 h-6 text-terracotta animate-pulse" />
              </div>
              <div className="w-48">
                <Progress value={progress} className="h-2" />
              </div>
              <span className="text-sm text-olive">{Math.round(progress)}% hochgeladen</span>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-terracotta" />
              </div>
              <span className="text-navy font-medium">Bild hochladen</span>
              <span className="text-sm text-olive">oder URL unten eingeben</span>
            </>
          )}
        </button>
      )}

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};
